/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Package, 
  Building2, 
  Flame, 
  Eye, 
  LogOut, 
  MapPin, 
  RefreshCw, 
  Search, 
  FileText, 
  Printer, 
  CheckCircle2, 
  Clock, 
  User, 
  X, 
  ArrowLeft, 
  ExternalLink,
  Layers,
  Truck,
  ShieldCheck,
  Activity,
  ChevronRight,
  Sparkles
} from 'lucide-react';
import { Folio, ProcessStage, Client, UserProfile, isClientRole, isClientUser as isClientUserHelper, isClientOperatorUser, shouldHidePricesForUser, CostCenter, Destination } from '../types.ts';
import { downloadFolioPDF } from '../utils/pdfGenerator.ts';
import TraceabilityStationManager from './TraceabilityStationManager.tsx';
import FolioRealtimeMapModal from './FolioRealtimeMapModal.tsx';

interface PublicTrackingPortalProps {
  initialFolioId?: string | null;
  initialFaseNumber?: number;
  initialClientId?: string;
  folios: Folio[];
  clients?: Client[];
  costCenters?: CostCenter[];
  destinations?: Destination[];
  currentUser?: UserProfile | null;
  onUpdateFolio?: (updated: Folio) => void;
  onBackToApp?: () => void;
  appLogoUrl?: string;
  isOnline?: boolean;
  isClientViewOnly?: boolean;
}

interface StationDef {
  key: string;
  stageKey: ProcessStage;
  faseNumber: number;
  label: string;
  icon: React.ElementType;
  desc: string;
}

const STATIONS: StationDef[] = [
  { 
    key: 'F1', 
    stageKey: 'RECOLECCION', 
    faseNumber: 1, 
    label: 'Recolección', 
    icon: Package, 
    desc: 'Chofer recolectando piezas y evidencia física en planta' 
  },
  { 
    key: 'F2', 
    stageKey: 'ENTRADA_CEDIS', 
    faseNumber: 2, 
    label: 'Entrada CEDIS', 
    icon: Building2, 
    desc: 'Ingreso al Centro de Distribución, pesaje y verificación' 
  },
  { 
    key: 'F3', 
    stageKey: 'DECAPADO_TERMICO_QUIMICO', 
    faseNumber: 3, 
    label: 'Decapado', 
    icon: Flame, 
    desc: 'Tratamiento químico y térmico de remoción superficial' 
  },
  { 
    key: 'F4', 
    stageKey: 'INSPECCION', 
    faseNumber: 4, 
    label: 'Inspección', 
    icon: Eye, 
    desc: 'Verificación de acabado superficial y certificación' 
  },
  { 
    key: 'F5', 
    stageKey: 'SALIDA_CEDIS', 
    faseNumber: 5, 
    label: 'Salida CEDIS', 
    icon: LogOut, 
    desc: 'Embarque final y traslado en ruta en tiempo real' 
  },
  { 
    key: 'F6', 
    stageKey: 'ENTREGA_PLANTA', 
    faseNumber: 6, 
    label: 'Entrega Planta', 
    icon: MapPin, 
    desc: 'Entregado formalmente en planta de destino final' 
  }
];

export const getStageBadgeInfo = (status: ProcessStage) => {
  switch (status) {
    case 'RECOLECCION':
      return { fase: 'F1', label: 'Recolección', color: 'bg-blue-600 text-white border-blue-700' };
    case 'ENTRADA_CEDIS':
      return { fase: 'F2', label: 'Entrada CEDIS', color: 'bg-indigo-600 text-white border-indigo-700' };
    case 'DECAPADO_TERMICO_QUIMICO':
    case 'DECAPADO_LAVADO':
      return { fase: 'F3', label: 'Decapado', color: 'bg-amber-600 text-white border-amber-700' };
    case 'INSPECCION':
    case 'RETRABAJO':
      return { fase: 'F4', label: 'Inspección', color: 'bg-purple-600 text-white border-purple-700' };
    case 'SALIDA_CEDIS':
      return { fase: 'F5', label: 'Salida CEDIS', color: 'bg-cyan-600 text-white border-cyan-700' };
    case 'ENTREGA_PLANTA':
    case 'DEVOLUCION':
      return { fase: 'F6', label: 'Entrega Planta', color: 'bg-emerald-600 text-white border-emerald-700' };
    default:
      return { fase: '--', label: String(status || '').replace(/_/g, ' '), color: 'bg-slate-700 text-white border-slate-800' };
  }
};

export default function PublicTrackingPortal({
  initialFolioId,
  initialFaseNumber,
  initialClientId,
  folios,
  clients = [],
  costCenters = [],
  destinations = [],
  currentUser,
  onUpdateFolio,
  onBackToApp,
  appLogoUrl = 'https://www.grupoisn.com.mx/wp-content/uploads/2025/12/cropped-WhatsApp-Image-2025-12-19-at-12.31.53.jpeg',
  isOnline = true,
  isClientViewOnly = false
}: PublicTrackingPortalProps) {
  
  // Find initial folio if passed
  const initialMatchedFolio = useMemo(() => {
    if (!initialFolioId) return null;
    return folios.find(f => f.id.trim().toUpperCase() === initialFolioId.trim().toUpperCase()) || null;
  }, [initialFolioId, folios]);

  // Client context determination
  const activeClientObj = useMemo(() => {
    if (initialClientId && clients && clients.length > 0) {
      const match = clients.find(c => c.id === initialClientId || c.code === initialClientId || c.name === initialClientId);
      if (match) return match;
    }
    if (initialMatchedFolio && initialMatchedFolio.clientName) {
      const match = clients?.find(c => c.name === initialMatchedFolio.clientName || c.id === initialMatchedFolio.clientId);
      if (match) return match;
    }
    if (initialClientId) {
      return { id: initialClientId, code: initialClientId, name: initialClientId } as Client;
    }
    if (clients && clients.length > 0) {
      return clients[0];
    }
    return null;
  }, [initialClientId, initialMatchedFolio, clients]);

  const activeClientName = useMemo(() => {
    if (activeClientObj) return activeClientObj.name;
    if (initialMatchedFolio && initialMatchedFolio.clientName) return initialMatchedFolio.clientName;
    return 'Cliente Seleccionado';
  }, [activeClientObj, initialMatchedFolio]);

  // Filter folios for this account/client context - Strictly synced with Mis Cargas y Envíos
  const clientFolios = useMemo(() => {
    if (initialClientId) {
      // When initialClientId is explicitly supplied from client submenu, use the scoped folios array directly
      return folios;
    }
    if (!activeClientName || activeClientName === 'TODOS LOS CLIENTES') {
      return folios;
    }
    const matched = folios.filter(f => 
      f.clientName === activeClientName || 
      (activeClientObj && (f.clientId === activeClientObj.id || f.clientId === activeClientObj.code)) ||
      (initialMatchedFolio && f.clientId === initialMatchedFolio.clientId) ||
      (f.clientName && activeClientName && f.clientName.toLowerCase() === activeClientName.toLowerCase()) ||
      (activeClientObj && f.clientId && activeClientObj.id && f.clientId.toLowerCase() === activeClientObj.id.toLowerCase())
    );
    return matched;
  }, [folios, activeClientName, activeClientObj, initialMatchedFolio, initialClientId]);

  const isClientUser = isClientOperatorUser(currentUser) || isClientRole(currentUser?.role) || Boolean(isClientUserHelper(currentUser)) || Boolean(isClientViewOnly);
  const hidePrices = shouldHidePricesForUser(currentUser) || isClientUser;
  const [activeTab, setActiveTab] = useState<'OPERATOR' | 'CLIENT'>(() => isClientUser ? 'CLIENT' : 'OPERATOR');

  useEffect(() => {
    if (isClientUser && activeTab !== 'CLIENT') {
      setActiveTab('CLIENT');
    }
  }, [isClientUser, activeTab]);
  const [selectedStationKey, setSelectedStationKey] = useState<string>(() => {
    if (initialFaseNumber) {
      const st = STATIONS.find(s => s.faseNumber === initialFaseNumber);
      if (st) return st.key;
    }
    if (initialMatchedFolio) {
      const st = STATIONS.find(s => s.stageKey === initialMatchedFolio.status);
      if (st) return st.key;
    }
    return 'F3'; // Default to Decapado (Fase 3) as in screenshot
  });

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedFolioModal, setSelectedFolioModal] = useState<Folio | null>(null);
  const [mapFolioModal, setMapFolioModal] = useState<Folio | null>(null);
  const [isPhaseFoliosModalOpen, setIsPhaseFoliosModalOpen] = useState<boolean>(false);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [lastSyncTime, setLastSyncTime] = useState<string>(new Date().toLocaleTimeString());

  // Sync simulation handler
  const handleSync = () => {
    setIsSyncing(true);
    setTimeout(() => {
      setIsSyncing(false);
      setLastSyncTime(new Date().toLocaleTimeString());
    }, 800);
  };

  // Calculate counts per station
  const stationCounts = useMemo(() => {
    const counts: Record<string, number> = {
      'ALL': clientFolios.length,
      'F1': 0,
      'F2': 0,
      'F3': 0,
      'F4': 0,
      'F5': 0,
      'F6': 0,
    };

    clientFolios.forEach(f => {
      if (f.status === 'RECOLECCION') counts['F1']++;
      else if (f.status === 'ENTRADA_CEDIS') counts['F2']++;
      else if (f.status === 'DECAPADO_TERMICO_QUIMICO' || f.status === 'DECAPADO_LAVADO') counts['F3']++;
      else if (f.status === 'INSPECCION' || f.status === 'RETRABAJO') counts['F4']++;
      else if (f.status === 'SALIDA_CEDIS') counts['F5']++;
      else if (f.status === 'ENTREGA_PLANTA' || f.status === 'DEVOLUCION') counts['F6']++;
    });

    return counts;
  }, [clientFolios]);

  // Currently active station definition
  const currentStationDef = useMemo(() => {
    if (selectedStationKey === 'ALL') {
      return {
        key: 'ALL',
        stageKey: 'INSPECCION' as ProcessStage,
        faseNumber: 0,
        label: 'Todas las Fases (General)',
        icon: Layers,
        desc: 'Visión consolidada y trazabilidad completa de todos los folios en todas sus fases de proceso'
      };
    }
    return STATIONS.find(s => s.key === selectedStationKey) || STATIONS[2];
  }, [selectedStationKey]);

  // Folios filtered by selected station and search query
  const filteredFolios = useMemo(() => {
    return clientFolios.filter(f => {
      // 1. Intelligent Global Search across ALL phases
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const rawNumber = q.replace(/^#/, '').replace(/^f-?/, '').trim();
        const fid = (f.id || '').toLowerCase();
        const fidClean = fid.replace(/^#/, '').replace(/^f-?/, '').trim();

        const idMatch = fid.includes(q) || (rawNumber.length > 0 && fidClean.includes(rawNumber));
        const matMatch = f.items && f.items.some(it => (it.description || '').toLowerCase().includes(q));
        const clientMatch = (f.clientName || '').toLowerCase().includes(q);
        const driverMatch = (f.driverName || '').toLowerCase().includes(q);
        const destMatch = (f.destination || '').toLowerCase().includes(q);
        const costCenterMatch = (f.costCenterName || '').toLowerCase().includes(q);
        const statusMatch = (f.status || '').toLowerCase().includes(q);

        return idMatch || matMatch || clientMatch || driverMatch || destMatch || costCenterMatch || statusMatch;
      }

      // 2. If 'ALL' is selected, show folios across every station
      if (selectedStationKey === 'ALL') {
        return true;
      }

      // 3. Stage match per selected station
      if (selectedStationKey === 'F1') return f.status === 'RECOLECCION';
      if (selectedStationKey === 'F2') return f.status === 'ENTRADA_CEDIS';
      if (selectedStationKey === 'F3') return f.status === 'DECAPADO_TERMICO_QUIMICO' || f.status === 'DECAPADO_LAVADO';
      if (selectedStationKey === 'F4') return f.status === 'INSPECCION' || f.status === 'RETRABAJO';
      if (selectedStationKey === 'F5') return f.status === 'SALIDA_CEDIS';
      if (selectedStationKey === 'F6') return f.status === 'ENTREGA_PLANTA' || f.status === 'DEVOLUCION';
      return true;
    });
  }, [clientFolios, selectedStationKey, searchQuery]);

  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#111215] text-slate-800 font-sans selection:bg-red-500/20 selection:text-red-800 pb-32 sm:pb-16 pb-safe touch-pan-y overscroll-y-contain">
      
      {/* TOP SYSTEM NAV TAB BAR FOR PUBLIC & OPERATIVE USE */}
      <div className="bg-[#18191d] text-white border-b border-slate-800 md:sticky md:top-0 z-40 shadow-xl px-4 py-3 pt-safe">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          
          <div className="flex items-center gap-3">
            <span className="px-2.5 py-1 bg-red-600 text-white font-black text-[10px] uppercase rounded-md tracking-wider font-mono">
              ALMACÉN OPERATIVO QR
            </span>
            <div className="text-xs text-slate-200 font-extrabold flex items-center gap-2">
              <span>SISTEMA DE TRAZABILIDAD Y CAMBIOS DE FASE EN PISO</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isClientUser ? (
              <div className="px-3.5 py-1.5 bg-red-600 text-white rounded-xl text-xs font-black shadow-lg shadow-red-950/50 flex items-center gap-2">
                <Eye className="h-3.5 w-3.5 text-white" />
                <span>Consulta General de Clientes</span>
                {activeClientName && activeClientName !== 'Cliente Seleccionado' && activeClientName !== 'TODOS LOS CLIENTES' && (
                  <span className="bg-red-800/80 border border-red-400/40 text-white text-[10px] px-2 py-0.5 rounded-md font-mono font-bold ml-1">
                    {activeClientName}
                  </span>
                )}
              </div>
            ) : (
              <>
                <button
                  onClick={() => setActiveTab('OPERATOR')}
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-black transition-all cursor-pointer flex items-center gap-2 ${
                    activeTab === 'OPERATOR'
                      ? 'bg-red-600 text-white shadow-lg shadow-red-950/50'
                      : 'bg-slate-800 text-slate-400 hover:text-white border border-slate-700'
                  }`}
                >
                  <Activity className="h-3.5 w-3.5 text-white" />
                  <span>Pantalla Operativa de Piso</span>
                </button>

                <button
                  onClick={() => setActiveTab('CLIENT')}
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-black transition-all cursor-pointer flex items-center gap-2 ${
                    activeTab === 'CLIENT'
                      ? 'bg-red-600 text-white shadow-lg shadow-red-950/50'
                      : 'bg-slate-800 text-slate-400 hover:text-white border border-slate-700'
                  }`}
                >
                  <Eye className="h-3.5 w-3.5 text-white" />
                  <span>Consulta General de Clientes</span>
                </button>
              </>
            )}

            {onBackToApp && (
              <button
                onClick={onBackToApp}
                className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-xl transition-all border border-slate-700 cursor-pointer ml-1"
                title="Volver al Portal Administrativo"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
          </div>

        </div>
      </div>

      {activeTab === 'OPERATOR' ? (
        <div className="max-w-7xl mx-auto px-3 sm:px-6 pt-4">
          <TraceabilityStationManager
            folios={clientFolios}
            clients={clients}
            currentUser={currentUser || {
              uid: 'op-piso',
              name: 'ING. PEDRO BORJA (ADMIN)',
              email: 'almacen@grupoisn.com.mx',
              role: 'OPERADOR',
              permissions: {
                canCreateFolios: true,
                canUpdateStages: true,
                canViewDashboard: true,
                canManageUsers: false,
                canBilling: false
              }
            }}
            onUpdateFolio={onUpdateFolio}
            isOnline={isOnline}
            selectedClientId={initialClientId}
            initialFaseNumber={initialFaseNumber}
            initialFolioId={initialFolioId}
            isPublicView={isClientUser}
            onBackToApp={onBackToApp}
          />
        </div>
      ) : (

      /* WRAPPER CONTAINER */
      <div className="max-w-7xl mx-auto px-3 sm:px-6 pt-4 space-y-5">
        
        {/* TOP HEADER PORTAL CARD (Matching screenshot dark top banner) */}
        <div className="bg-[#1e1e24] text-white rounded-3xl p-5 md:p-6 shadow-2xl relative overflow-hidden border border-slate-800">
          
          {/* Subtle Ambient Glow Effect */}
          <div className="absolute right-0 top-0 bottom-0 w-1/2 bg-radial from-red-600/10 via-slate-900/0 to-transparent pointer-events-none"></div>

          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 relative z-10">
            
            {/* Left Header Brand & Title */}
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                {onBackToApp && (
                  <button
                    onClick={onBackToApp}
                    className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-all border border-slate-700 cursor-pointer"
                    title="Volver al Portal Administrativo"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                )}
                
                <span className="px-2.5 py-0.5 bg-red-900/60 border border-red-700/80 text-red-300 text-[10px] font-black uppercase rounded-md tracking-wider">
                  PORTAL DE CLIENTES
                </span>
                
                <div className="flex items-center gap-1.5 text-slate-400 text-[11px] font-mono tracking-wider">
                  <span className="font-bold text-slate-200">SNF</span>
                  <span>|</span>
                  <span className="font-bold text-slate-200">SIC PRO SNF</span>
                  <span className="hidden sm:inline text-slate-500 text-[9px]">SISTEMA INTEGRAL DE CONTROL</span>
                </div>
              </div>

              <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight font-display flex items-center gap-3">
                <Activity className="h-7 w-7 text-red-500 animate-pulse" />
                Control y Trazabilidad Operativa
              </h1>

              <p className="text-xs text-slate-300 max-w-3xl leading-relaxed">
                Monitoree en vivo el avance progresivo de sus folios de carga. Observe sus materiales moviéndose de manera sincronizada en tiempo real a través de las cinco etapas del proceso de decapado.
              </p>
            </div>

            {/* Right Header Live Sync Status */}
            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <div className="bg-[#121316] border border-slate-800 px-3.5 py-2 rounded-2xl flex items-center gap-2.5">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                </span>
                <div className="text-[10px] font-mono">
                  <span className="text-slate-400 block font-semibold leading-none uppercase">SINCRONIZACIÓN</span>
                  <span className="text-emerald-400 font-extrabold leading-tight">En Vivo con Patio ({lastSyncTime})</span>
                </div>
              </div>

              <button
                onClick={handleSync}
                disabled={isSyncing}
                className="bg-red-700 hover:bg-red-600 active:scale-95 text-white font-black text-xs px-4 py-2.5 rounded-xl transition-all shadow-lg shadow-red-700/30 flex items-center gap-2 cursor-pointer border border-red-500/30"
              >
                <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
                SINCRONIZAR
              </button>
            </div>

          </div>
        </div>

        {/* ACCOUNT HEADER CARD */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-200/80 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-red-50 text-red-600 border border-red-100 flex items-center justify-center shrink-0">
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <span className="text-[10px] font-bold text-slate-400 tracking-wider uppercase block">RAZÓN SOCIAL DE CUENTA</span>
              <h2 className="text-base font-black text-slate-900 tracking-tight font-display">
                {activeClientName}
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-4 text-xs font-mono">
            <div>
              <span className="text-[10px] text-slate-400 block font-bold uppercase">IDENTIFICACIÓN NIT / RFC</span>
              <span className="text-slate-700 font-extrabold">{activeClientObj?.code || '01'}</span>
            </div>

            <div className="h-8 w-px bg-slate-200 hidden sm:block"></div>

            <div className="bg-slate-50 border border-slate-200/80 px-4 py-1.5 rounded-2xl font-bold text-slate-800">
              <span className="text-red-700 font-black text-sm mr-1.5">{clientFolios.length}</span>
              <span className="text-slate-600 text-[11px]">Folios Totales Activos</span>
            </div>
          </div>
        </div>

        {/* PROCESS TIMELINE CONTAINER (Línea de Proceso Decapado) */}
        <div className="bg-white/95 backdrop-blur-md rounded-2xl p-5 sm:p-6 border border-slate-200/90 shadow-md space-y-6 md:sticky md:top-[55px] z-20 relative">
          
          {/* Timeline Title */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-amber-500" />
                <h3 className="text-base font-extrabold text-slate-900 tracking-tight">
                  Línea de Proceso Decapado y Trazabilidad en Piso
                </h3>
              </div>
              <p className="text-xs text-slate-500 max-w-3xl">
                Haga clic sobre cualquiera de las estaciones o seleccione &ldquo;Todas las Fases&rdquo; para desplegar de forma inmediata los folios certificados.
              </p>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setSelectedStationKey('ALL')}
                className={`px-3.5 py-2 rounded-xl text-xs font-black transition-all flex items-center gap-2 cursor-pointer shadow-sm ${
                  selectedStationKey === 'ALL'
                    ? 'bg-red-600 text-white shadow-red-900/30 ring-2 ring-red-400'
                    : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300'
                }`}
              >
                <Layers className="h-4 w-4" />
                <span>Ver Todas las Fases ({clientFolios.length})</span>
              </button>
            </div>
          </div>

          {/* 6-STATIONS WORKFLOW TIMELINE STEPPER */}
          {/* DESKTOP VIEW: Horizontal Timeline */}
          <div className="hidden md:block relative py-4 px-2">
            {/* Background connecting bar */}
            <div className="absolute top-1/2 left-10 right-10 -translate-y-1/2 h-1 bg-slate-100 rounded-full z-0"></div>
            
            {/* Animated Progress Beam */}
            <motion.div 
              className="absolute top-1/2 left-10 -translate-y-1/2 h-1 bg-gradient-to-r from-red-500 via-amber-500 to-emerald-500 rounded-full z-0"
              initial={{ width: '0%' }}
              animate={{ 
                width: `${((STATIONS.findIndex(s => s.key === selectedStationKey) + 1) / STATIONS.length) * 100}%` 
              }}
              transition={{ type: 'spring', stiffness: 120, damping: 20 }}
            />

            <div className="grid grid-cols-6 gap-4 relative z-10">
              {STATIONS.map((st) => {
                const IconComponent = st.icon;
                const count = stationCounts[st.key] || 0;
                const isSelected = selectedStationKey === st.key;

                return (
                  <motion.div
                    key={st.key}
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.96 }}
                    onClick={() => {
                      setSelectedStationKey(st.key);
                      setIsPhaseFoliosModalOpen(true);
                    }}
                    className={`flex flex-col items-center text-center cursor-pointer p-3 rounded-2xl transition-all relative ${
                      isSelected 
                        ? 'bg-emerald-50/80 border-2 border-emerald-500 shadow-lg shadow-emerald-500/10 ring-2 ring-emerald-400/30' 
                        : 'bg-white border border-slate-200/60 hover:border-slate-300 hover:bg-slate-50/50 shadow-xs'
                    }`}
                  >
                    {/* Badge Count on Top */}
                    <motion.div 
                      initial={{ scale: 0.8 }}
                      animate={{ scale: count > 0 ? 1 : 0.85 }}
                      className={`absolute -top-2.5 px-2 py-0.5 rounded-full text-[11px] font-black font-mono shadow-sm z-20 ${
                        count > 0 
                          ? 'bg-red-600 text-white ring-2 ring-white' 
                          : 'bg-slate-200 text-slate-500'
                      }`}
                    >
                      {count}
                    </motion.div>

                    {/* Circle Icon Container */}
                    <div className={`h-14 w-14 rounded-full flex items-center justify-center transition-all mb-2 relative ${
                      isSelected
                        ? 'bg-emerald-100 text-emerald-800 ring-4 ring-emerald-400/30'
                        : count > 0
                          ? 'bg-slate-100 text-slate-700'
                          : 'bg-slate-50 text-slate-400'
                    }`}>
                      <IconComponent className="h-6 w-6" />
                      
                      {isSelected && (
                        <span className="absolute inset-0 rounded-full border-2 border-emerald-500 animate-ping opacity-30"></span>
                      )}
                    </div>

                    {/* Station Name */}
                    <span className={`text-xs font-black tracking-tight ${isSelected ? 'text-emerald-900' : 'text-slate-800'}`}>
                      {st.label}
                    </span>
                    <span className={`text-[10px] font-mono mt-0.5 ${isSelected ? 'text-emerald-600 font-bold' : 'text-slate-400'}`}>
                      Estación
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* MOBILE VIEW: Vertical Linear Pipeline (Sin desplazamiento horizontal de costado) */}
          <div className="block md:hidden relative py-1 space-y-2.5">
            <div className="flex items-center justify-between px-1 pb-1">
              <span className="text-[11px] font-bold text-slate-500">
                Flujo Secuencial Lineal:
              </span>
              <button
                onClick={() => setSelectedStationKey(selectedStationKey === 'ALL' ? 'F3' : 'ALL')}
                className={`text-[10px] font-mono font-bold px-2.5 py-1 rounded-md border cursor-pointer flex items-center gap-1.5 transition-all ${
                  selectedStationKey === 'ALL' 
                    ? 'bg-red-600 text-white border-red-700 shadow-xs' 
                    : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                }`}
              >
                <Layers className="h-3 w-3" />
                <span>{selectedStationKey === 'ALL' ? '✓ Todas las Fases' : `Ver Todas (${clientFolios.length})`}</span>
              </button>
            </div>

            <div className="space-y-2 relative">
              {/* Vertical connecting progress track */}
              <div className="absolute left-6 top-5 bottom-5 w-0.5 bg-gradient-to-b from-red-500 via-amber-500 to-emerald-500 z-0"></div>

              {STATIONS.map((st) => {
                const IconComponent = st.icon;
                const count = stationCounts[st.key] || 0;
                const isSelected = selectedStationKey === st.key;

                return (
                  <motion.div
                    key={st.key}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setSelectedStationKey(st.key);
                      setIsPhaseFoliosModalOpen(true);
                    }}
                    className={`w-full p-3 rounded-2xl transition-all relative z-10 flex items-center justify-between gap-3 border cursor-pointer ${
                      isSelected
                        ? 'bg-emerald-50/95 border-2 border-emerald-500 shadow-md ring-2 ring-emerald-400/20'
                        : 'bg-white border-slate-200/90 hover:bg-slate-50 shadow-xs'
                    }`}
                  >
                    {/* Left: Icon circle & labels */}
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`h-11 w-11 rounded-2xl flex items-center justify-center shrink-0 relative ${
                        isSelected
                          ? 'bg-emerald-600 text-white shadow-sm ring-2 ring-emerald-400/40'
                          : count > 0
                            ? 'bg-slate-800 text-slate-100'
                            : 'bg-slate-100 text-slate-400'
                      }`}>
                        <IconComponent className="h-5 w-5" />
                      </div>

                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-black uppercase text-slate-400 font-mono">
                            ESTACIÓN
                          </span>
                          {isSelected && (
                            <span className="text-[9px] font-black px-1.5 py-0.2 bg-emerald-600 text-white rounded font-mono">
                              SELECCIONADA
                            </span>
                          )}
                        </div>
                        <h4 className={`text-xs font-black leading-tight truncate ${isSelected ? 'text-emerald-950' : 'text-slate-900'}`}>
                          {st.label}
                        </h4>
                        <p className="text-[10px] text-slate-500 line-clamp-1 mt-0.5">
                          {st.desc}
                        </p>
                      </div>
                    </div>

                    {/* Right: Folio count & Chevron */}
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`px-2.5 py-1 rounded-xl text-xs font-black font-mono flex items-center gap-1 shadow-xs ${
                        count > 0 
                          ? 'bg-red-600 text-white animate-pulse' 
                          : 'bg-slate-100 text-slate-500'
                      }`}>
                        <span>{count}</span>
                        <span className="text-[9px] font-sans font-bold">fol.</span>
                      </span>
                      <ChevronRight className={`h-4 w-4 ${isSelected ? 'text-emerald-600 translate-x-0.5' : 'text-slate-400'}`} />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

        </div>

        {/* SELECTED STATION BAR & SEARCH */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-200/80 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">VISTA DE TRAZABILIDAD:</span>
                {searchQuery.trim() ? (
                  <span className="text-xs font-black text-amber-700 font-mono uppercase bg-amber-50 px-2.5 py-0.5 rounded-md border border-amber-200">
                    BÚSQUEDA GLOBAL: &ldquo;{searchQuery}&rdquo;
                  </span>
                ) : (
                  <span className="text-xs font-black text-red-700 font-mono uppercase bg-red-50 px-2 py-0.5 rounded-md border border-red-100">
                    {currentStationDef.label.toUpperCase()}
                  </span>
                )}
              </div>
              <h3 className="text-sm font-black text-slate-900 tracking-tight mt-1">
                {searchQuery.trim()
                  ? `Folios encontrados en todas las fases (${filteredFolios.length})`
                  : selectedStationKey === 'ALL'
                    ? `Todos los Folios Activos en Piso (${filteredFolios.length})`
                    : `Folios Activos en esta Estación (${filteredFolios.length})`
                }
              </h3>
            </div>

            {/* Direct Popup Modal Trigger Button */}
            <button
              onClick={() => setIsPhaseFoliosModalOpen(true)}
              className="px-3.5 py-2 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white rounded-xl text-xs font-black flex items-center gap-1.5 shadow-md shadow-red-900/30 cursor-pointer active:scale-95 transition-all self-start sm:self-auto"
              title="Abrir ventana emergente con lista detallada completa"
            >
              <Sparkles className="w-3.5 h-3.5 text-amber-200" />
              <span>Ver Detalle en Ventana Emergente ({filteredFolios.length})</span>
            </button>
          </div>

          {/* Search bar */}
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 w-full md:w-80 focus-within:ring-2 focus-within:ring-red-500/20 focus-within:border-red-500 transition-all">
            <Search className="h-4 w-4 text-slate-400 shrink-0" />
            <input
              type="text"
              placeholder="Buscar folio (ej. 19769636), material..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent text-xs text-slate-800 placeholder-slate-400 focus:outline-hidden w-full font-medium"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="text-slate-400 hover:text-slate-600">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* FOLIOS CARDS GRID */}
        {filteredFolios.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-slate-200/80 shadow-xs space-y-3">
            <div className="h-12 w-12 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mx-auto">
              <Package className="h-6 w-6" />
            </div>
            <h4 className="text-sm font-bold text-slate-800">No se encontraron folios en esta fase</h4>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              Actualmente no hay cargas registradas en la estación <span className="font-bold">{currentStationDef.label}</span>. Seleccione otra fase del proceso arriba para consultar sus folios.
            </p>
          </div>
        ) : (
          <motion.div 
            layout
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            <AnimatePresence mode="popLayout">
              {filteredFolios.map((folio) => {
                const mainItem = folio.items[0];
                const totalPieces = folio.items.reduce((acc, it) => acc + (it.quantity || 0), 0);
                const hasWeight = (folio as any).totalWeight || 0;

                return (
                  <motion.div
                    key={folio.id}
                    layout
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="bg-white rounded-2xl p-5 border border-slate-200/90 hover:border-slate-300 shadow-xs hover:shadow-md transition-all flex flex-col justify-between gap-4 group"
                  >
                    {/* Top Row: Folio ID Badge & Status Tag & Date */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="bg-slate-900 text-white font-black text-xs px-2.5 py-1 rounded-lg font-mono">
                            #{folio.id}
                          </span>
                          {(() => {
                            const badge = getStageBadgeInfo(folio.status);
                            return (
                              <span className={`border text-[10px] font-black px-2 py-0.5 rounded-md uppercase font-mono shadow-xs ${badge.color}`}>
                                {badge.fase}: {badge.label}
                              </span>
                            );
                          })()}
                          {folio.costCenterName && (
                            <span className="bg-slate-100 text-slate-700 text-[9px] font-bold px-1.5 py-0.5 rounded-md truncate max-w-[130px]" title={folio.costCenterName}>
                              {folio.costCenterName}
                            </span>
                          )}
                        </div>
                        <span className="text-[11px] font-medium text-slate-400 font-mono">
                          {new Date(folio.createdDate).toLocaleDateString()}
                        </span>
                      </div>

                      {/* Main Description */}
                      <h4 className="text-sm font-extrabold text-slate-900 leading-snug line-clamp-2 min-h-[40px]">
                        {mainItem ? mainItem.description : 'Piezas de Decapado General'}
                      </h4>
                    </div>

                    {/* Key Metrics: Total Piezas & Peso Neto */}
                    <div className="grid grid-cols-2 gap-4 py-2 border-y border-slate-100 text-xs">
                      <div>
                        <span className="text-[10px] font-bold text-slate-400 uppercase block">TOTAL PIEZAS</span>
                        <span className="font-extrabold text-slate-900 text-sm">{totalPieces} PZAS</span>
                      </div>

                      <div>
                        <span className="text-[10px] font-bold text-slate-400 uppercase block">PESO NETO</span>
                        <span className="font-extrabold text-slate-900 text-sm">
                          {hasWeight > 0 ? `${hasWeight} KGS` : 'N/A KGS'}
                        </span>
                      </div>
                    </div>

                    {/* Bottom Actions Row */}
                    <div className="flex items-center justify-between gap-2 pt-1">
                      <div className="flex items-center gap-1.5 text-[11px] text-slate-500 font-medium truncate">
                        <span className="h-2 w-2 rounded-full bg-red-600 shrink-0"></span>
                        <span className="truncate">En ruta: Llegada...</span>
                      </div>

                      <div className="flex items-center gap-1.5 shrink-0">
                        {!isClientUser && (
                          <button
                            onClick={() => {
                              setSelectedStationKey(
                                folio.status === 'RECOLECCION' ? 'F1' :
                                folio.status === 'ENTRADA_CEDIS' ? 'F2' :
                                (folio.status === 'DECAPADO_TERMICO_QUIMICO' || folio.status === 'DECAPADO_LAVADO') ? 'F3' :
                                (folio.status === 'INSPECCION' || folio.status === 'RETRABAJO') ? 'F4' :
                                folio.status === 'SALIDA_CEDIS' ? 'F5' : 'F6'
                              );
                              setActiveTab('OPERATOR');
                            }}
                            className="bg-red-700 hover:bg-red-600 active:scale-95 text-white text-xs font-black px-2.5 py-1.5 rounded-lg transition-all flex items-center gap-1 cursor-pointer shadow-xs border border-red-500"
                            title="Confirmar cambio de fase en pantalla de piso"
                          >
                            <Activity className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">Confirmar Fase</span>
                          </button>
                        )}

                        <button
                          onClick={() => setMapFolioModal(folio)}
                          className="bg-emerald-50 hover:bg-emerald-100 text-emerald-900 border border-emerald-300 text-xs font-black px-2.5 py-1.5 rounded-lg transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs active:scale-95"
                          title="Ver mapa de ubicación GPS en tiempo real"
                        >
                          <Truck className="h-3.5 w-3.5 text-emerald-600 animate-pulse" />
                          <span className="hidden sm:inline">Mapa</span>
                        </button>

                        <button
                          onClick={() => downloadFolioPDF(folio, { hidePrices: isClientUser })}
                          className="bg-red-50 hover:bg-red-100 text-red-700 text-xs font-extrabold px-2 py-1.5 rounded-lg transition-all flex items-center gap-1 cursor-pointer border border-red-200"
                          title="Descargar Remisión PDF"
                        >
                          <Printer className="h-3.5 w-3.5" /> PDF
                        </button>

                        <button
                          onClick={() => setSelectedFolioModal(folio)}
                          className="bg-white hover:bg-slate-50 text-slate-800 text-xs font-black px-2.5 py-1.5 rounded-lg transition-all flex items-center gap-1 cursor-pointer border border-slate-200 shadow-2xs group-hover:border-slate-400"
                        >
                          Ver Detalles <ChevronRight className="h-3.5 w-3.5 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
                        </button>
                      </div>
                    </div>

                  </motion.div>
                );
              })}
            </AnimatePresence>
          </motion.div>
        )}

      </div>
      )}

      {/* DETAILED FOLIO MODAL ("Ver Detalles >") */}
      <AnimatePresence>
        {selectedFolioModal && (
          <div className="fixed inset-0 z-[99999] flex flex-col justify-end sm:justify-center items-center p-0 sm:p-4 md:p-6 bg-slate-950/85 backdrop-blur-md overflow-hidden animate-in fade-in duration-200">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="bg-white rounded-t-3xl sm:rounded-3xl border-t sm:border border-slate-200 shadow-2xl max-w-4xl w-full flex flex-col h-[94vh] sm:h-auto sm:max-h-[90vh] overflow-hidden"
            >
              {/* Mobile grab handle */}
              <div className="bg-[#181920] pt-2 pb-0 flex justify-center sm:hidden shrink-0">
                <div className="w-12 h-1 bg-white/20 rounded-full" />
              </div>

              {/* Modal Header */}
              <div className="bg-[#181920] text-white pt-[calc(env(safe-area-inset-top,0px)+8px)] sm:pt-4 px-4 sm:px-6 pb-3.5 sm:pb-4 flex items-center justify-between gap-3 border-b border-slate-800 shrink-0 sticky top-0 z-30 shadow-md">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2.5 py-0.5 bg-red-900/60 border border-red-700 text-red-300 text-[10px] font-black uppercase rounded-md font-mono">
                      DETALLE CERTIFICADO DE FOLIO
                    </span>
                    <span className="text-xs text-slate-400 font-mono">
                      {new Date(selectedFolioModal.createdDate).toLocaleDateString()}
                    </span>
                  </div>
                  <h2 className="text-lg sm:text-2xl font-black text-white font-mono mt-0.5 truncate">
                    FOLIO #{selectedFolioModal.id}
                  </h2>
                </div>

                <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                  <button
                    onClick={() => {
                      const currentF = selectedFolioModal;
                      setSelectedFolioModal(null);
                      setMapFolioModal(currentF);
                    }}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-black px-2.5 sm:px-3.5 py-2 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer shadow-sm active:scale-95 border border-emerald-500/50"
                    title="Ver Mapa en Tiempo Real"
                  >
                    <Truck className="h-4 w-4 animate-pulse text-emerald-200" />
                    <span className="hidden sm:inline">Mapa GPS</span>
                  </button>

                  <button
                    onClick={() => downloadFolioPDF(selectedFolioModal, { hidePrices: isClientUser })}
                    className="bg-red-700 hover:bg-red-600 text-white text-xs font-bold px-2.5 sm:px-3 py-2 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer"
                    title="Descargar Remisión PDF"
                  >
                    <Printer className="h-4 w-4" /> 
                    <span className="hidden sm:inline">PDF</span>
                  </button>

                  <button
                    onClick={() => setSelectedFolioModal(null)}
                    aria-label="Cerrar modal de detalle"
                    className="h-10 w-10 sm:h-9 sm:w-9 rounded-full bg-white/10 hover:bg-white/20 active:scale-95 text-white flex items-center justify-center cursor-pointer border border-white/20 shrink-0 transition-all shadow-xs"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6 space-y-6 text-slate-800 custom-scrollbar min-h-0">
                
                {/* GPS Live Real-time Map Direct Banner Card */}
                <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-emerald-950 text-white p-4.5 rounded-2xl border border-emerald-500/30 shadow-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-3 bg-emerald-500/20 border border-emerald-400/40 rounded-2xl text-emerald-400 shrink-0 shadow-inner">
                      <MapPin className="h-6 w-6 animate-bounce" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-emerald-400 animate-ping shrink-0"></span>
                        <span className="text-[10px] font-black uppercase text-emerald-400 font-mono tracking-wider truncate">
                          RASTREO Y MONITOREO GPS EN VIVO
                        </span>
                      </div>
                      <h4 className="text-sm sm:text-base font-extrabold text-white mt-0.5 font-mono truncate">
                        Ubicación Exacta de Folio #{selectedFolioModal.id}
                      </h4>
                      <p className="text-xs text-slate-300 font-medium mt-0.5 truncate">
                        Chofer: <strong className="text-white font-mono">{selectedFolioModal.driverName || 'Operador en Ruta'}</strong> • Unidad: <strong className="text-white font-mono">{selectedFolioModal.economicNumber || 'TRK-NL'}</strong>
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      const currentF = selectedFolioModal;
                      setSelectedFolioModal(null);
                      setMapFolioModal(currentF);
                    }}
                    className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs px-4 py-2.5 rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer shrink-0 shadow-md active:scale-95"
                  >
                    <Truck className="h-4 w-4" />
                    <span>Abrir Mapa Interactivo</span>
                    <ExternalLink className="h-3.5 w-3.5 opacity-80" />
                  </button>
                </div>
                
                {/* General Info Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-200/80 text-xs">
                  <div>
                    <span className="text-slate-400 font-bold block text-[10px] uppercase">Razón Social Cliente</span>
                    <p className="font-extrabold text-slate-900 text-sm mt-0.5">{selectedFolioModal.clientName}</p>
                  </div>

                  <div>
                    <span className="text-slate-400 font-bold block text-[10px] uppercase">Centro de Costos</span>
                    <p className="font-extrabold text-slate-800 mt-0.5">{selectedFolioModal.costCenterName}</p>
                  </div>

                  <div>
                    <span className="text-slate-400 font-bold block text-[10px] uppercase">Estatus Trazabilidad</span>
                    <span className="inline-block font-black text-emerald-700 uppercase bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded-md mt-0.5 font-mono">
                      {selectedFolioModal.status.replace(/_/g, ' ')}
                    </span>
                  </div>

                  <div>
                    <span className="text-slate-400 font-bold block text-[10px] uppercase">Chofer Autorizado</span>
                    <p className="font-bold text-slate-800 mt-0.5">{selectedFolioModal.driverName || 'No asignado'}</p>
                  </div>

                  <div>
                    <span className="text-slate-400 font-bold block text-[10px] uppercase">N° Económico Unidad</span>
                    <p className="font-bold text-slate-800 font-mono mt-0.5">{selectedFolioModal.economicNumber || 'N/A'}</p>
                  </div>

                  <div>
                    <span className="text-slate-400 font-bold block text-[10px] uppercase">Registrado Por</span>
                    <p className="font-bold text-slate-800 mt-0.5">{selectedFolioModal.createdBy}</p>
                  </div>
                </div>

                {/* Items Breakdown Table */}
                <div className="space-y-3">
                  <h3 className="text-xs font-black uppercase tracking-wider text-slate-500 border-b border-slate-200 pb-2">
                    Materiales y Cantidades Certificadas
                  </h3>

                  <div className="divide-y divide-slate-100 border border-slate-200 rounded-2xl overflow-hidden">
                    {selectedFolioModal.items.map((it, idx) => (
                      <div key={idx} className="p-3.5 bg-white flex items-center justify-between gap-4 text-xs">
                        <div>
                          <p className="font-bold text-slate-900">{it.description}</p>
                          <span className="text-[10px] text-slate-400 font-mono">Código/ID Item: {it.goodsItemId}</span>
                        </div>
                        <span className="font-black text-red-700 bg-red-50 border border-red-100 px-3 py-1 rounded-xl font-mono text-sm">
                          {it.quantity} PZAS
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Process Timeline History */}
                <div className="space-y-3">
                  <h3 className="text-xs font-black uppercase tracking-wider text-slate-500 border-b border-slate-200 pb-2">
                    Historial de Fases Registradas
                  </h3>

                  <div className="space-y-2">
                    {selectedFolioModal.history.map((entry, idx) => (
                      <div key={idx} className="bg-slate-50 p-3 rounded-xl border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                        <div className="flex items-center gap-2.5">
                          <span className="h-6 w-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-[10px] font-mono shrink-0">
                            ✓
                          </span>
                          <div>
                            <span className="font-bold text-slate-900 block">{entry.stage.replace(/_/g, ' ')}</span>
                            <span className="text-[10px] text-slate-500">Operador: {entry.operatorName}</span>
                          </div>
                        </div>

                        <span className="text-[11px] text-slate-500 font-mono bg-white px-2.5 py-1 rounded-lg border border-slate-200 self-start sm:self-auto">
                          🕒 {new Date(entry.timestamp).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Evidence Photo if present */}
                {selectedFolioModal.photoUrl && (
                  <div className="space-y-3">
                    <h3 className="text-xs font-black uppercase tracking-wider text-slate-500 border-b border-slate-200 pb-2">
                      Evidencia Fotográfica de Recolección
                    </h3>
                    <div className="rounded-2xl overflow-hidden border border-slate-200 aspect-video max-w-md bg-slate-900">
                      <img 
                        src={selectedFolioModal.photoUrl} 
                        alt="Evidencia Carga" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                )}

              </div>

              {/* Modal Footer */}
              <div className="bg-slate-50 p-3.5 sm:p-4 pb-[calc(env(safe-area-inset-bottom,0px)+16px)] sm:pb-4 border-t border-slate-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 shrink-0 sticky bottom-0 z-30 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]">
                {!isClientUser ? (
                  <button
                    onClick={() => {
                      const targetFolio = selectedFolioModal;
                      setSelectedFolioModal(null);
                      setSelectedStationKey(
                        targetFolio.status === 'RECOLECCION' ? 'F1' :
                        targetFolio.status === 'ENTRADA_CEDIS' ? 'F2' :
                        (targetFolio.status === 'DECAPADO_TERMICO_QUIMICO' || targetFolio.status === 'DECAPADO_LAVADO') ? 'F3' :
                        (targetFolio.status === 'INSPECCION' || targetFolio.status === 'RETRABAJO') ? 'F4' :
                        targetFolio.status === 'SALIDA_CEDIS' ? 'F5' : 'F6'
                      );
                      setActiveTab('OPERATOR');
                    }}
                    className="bg-red-700 hover:bg-red-600 active:scale-95 text-white text-xs font-black px-4 py-3 sm:py-2.5 rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer shadow-md border border-red-500"
                  >
                    <Activity className="h-4 w-4" />
                    <span>Confirmar Cambio de Fase en Piso</span>
                  </button>
                ) : (
                  <div className="hidden sm:block"></div>
                )}

                <button
                  onClick={() => setSelectedFolioModal(null)}
                  className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 active:scale-95 text-white text-xs font-black px-6 py-3 sm:py-2.5 rounded-xl transition-all cursor-pointer flex items-center justify-center gap-2 shadow-sm"
                >
                  <X className="h-4 w-4" />
                  <span>Cerrar</span>
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* REAL-TIME FOLIO GPS MAP MODAL */}
      <AnimatePresence>
        {mapFolioModal && (
          <FolioRealtimeMapModal
            folio={mapFolioModal}
            costCenters={costCenters}
            destinations={destinations}
            currentUser={currentUser}
            isClientViewOnly={isClientUser}
            onClose={() => setMapFolioModal(null)}
            onOpenDetails={(f) => {
              setMapFolioModal(null);
              setSelectedFolioModal(f);
            }}
          />
        )}
      </AnimatePresence>

      {/* VENTANA EMERGENTE: DETALLE DE FOLIOS ACTIVOS POR FASE */}
      <AnimatePresence>
        {isPhaseFoliosModalOpen && (
          <div className="fixed inset-0 z-[99999] flex flex-col justify-end sm:justify-center items-center p-0 sm:p-4 md:p-6 bg-slate-950/85 backdrop-blur-md overflow-hidden animate-in fade-in duration-200">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-t-3xl sm:rounded-3xl border-t sm:border border-slate-200 shadow-2xl max-w-4xl w-full flex flex-col h-[94vh] sm:h-auto sm:max-h-[90vh] overflow-hidden"
            >
              {/* Mobile grab handle */}
              <div className="bg-[#181920] pt-2 pb-0 flex justify-center sm:hidden shrink-0">
                <div className="w-12 h-1 bg-white/20 rounded-full" />
              </div>

              {/* Modal Header */}
              <div className="bg-[#181920] text-white pt-[calc(env(safe-area-inset-top,0px)+8px)] sm:pt-4 px-4 sm:px-6 pb-3 sm:pb-4 border-b border-slate-800 shrink-0 sticky top-0 z-30 shadow-md">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-2xl bg-gradient-to-br from-red-600 to-rose-600 flex items-center justify-center text-white shadow-lg shadow-red-950/60 shrink-0">
                      {React.createElement(currentStationDef.icon, { className: 'w-4 h-4 sm:w-5 sm:h-5' })}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                        <span className="px-2 py-0.5 rounded bg-red-950 text-red-300 font-mono text-[9px] sm:text-[10px] font-black border border-red-800/80 uppercase">
                          ESTACIÓN • {currentStationDef.label.toUpperCase()}
                        </span>
                        <span className="text-[10px] sm:text-[11px] text-slate-400 font-mono hidden sm:inline truncate">
                          {activeClientName}
                        </span>
                      </div>
                      <h3 className="text-sm sm:text-lg font-black text-white tracking-tight mt-0.5 truncate">
                        Folios en {currentStationDef.label}
                      </h3>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                    <span className="px-2.5 sm:px-3 py-1 bg-red-600/30 text-red-300 border border-red-500/40 rounded-xl text-xs font-mono font-black">
                      {filteredFolios.length} {filteredFolios.length === 1 ? 'Folio' : 'Folios'}
                    </span>
                    <button
                      onClick={() => setIsPhaseFoliosModalOpen(false)}
                      aria-label="Cerrar ventana de folios"
                      className="h-10 w-10 sm:h-9 sm:w-9 rounded-full bg-white/10 hover:bg-white/20 active:scale-95 text-white flex items-center justify-center cursor-pointer border border-white/20 shrink-0 transition-all shadow-xs"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Subtitle / Phase description */}
                <p className="text-[11px] sm:text-xs text-slate-400 mt-1.5 sm:mt-2 line-clamp-1">
                  {currentStationDef.desc}
                </p>

                {/* Quick Stage Switcher Tabs inside Modal */}
                <div className="flex items-center gap-1.5 overflow-x-auto pt-2.5 sm:pt-3 pb-0.5 scrollbar-none">
                  <button
                    onClick={() => setSelectedStationKey('ALL')}
                    className={`px-2.5 sm:px-3 py-1.5 rounded-xl text-[11px] sm:text-xs font-bold shrink-0 transition-all flex items-center gap-1.5 cursor-pointer ${
                      selectedStationKey === 'ALL'
                        ? 'bg-emerald-500 text-slate-950 shadow-md font-black'
                        : 'bg-slate-800/90 text-slate-300 hover:bg-slate-700 border border-slate-700'
                    }`}
                  >
                    <Layers className="w-3.5 h-3.5" />
                    <span>Todas las Fases</span>
                    <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-mono font-black ${
                      selectedStationKey === 'ALL' ? 'bg-slate-950 text-white' : 'bg-red-600 text-white'
                    }`}>
                      {clientFolios.length}
                    </span>
                  </button>

                  {STATIONS.map((st) => {
                    const count = stationCounts[st.key] || 0;
                    const isSelected = selectedStationKey === st.key;
                    return (
                      <button
                        key={st.key}
                        onClick={() => setSelectedStationKey(st.key)}
                        className={`px-2.5 sm:px-3 py-1.5 rounded-xl text-[11px] sm:text-xs font-bold shrink-0 transition-all flex items-center gap-1.5 cursor-pointer ${
                          isSelected
                            ? 'bg-emerald-500 text-slate-950 shadow-md font-black'
                            : 'bg-slate-800/90 text-slate-300 hover:bg-slate-700 border border-slate-700'
                        }`}
                      >
                        <span>{st.label}</span>
                        <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-mono font-black ${
                          isSelected
                            ? 'bg-slate-950 text-white'
                            : count > 0 ? 'bg-red-600 text-white' : 'bg-slate-700 text-slate-400'
                        }`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* In-Modal Search bar */}
              <div className="p-3 sm:px-5 bg-slate-50 border-b border-slate-200 shrink-0">
                <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-red-500/20 focus-within:border-red-500 transition-all">
                  <Search className="h-4 w-4 text-slate-400 shrink-0" />
                  <input
                    type="text"
                    placeholder="Filtrar folios en esta estación por ID, descripción o cliente..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="bg-transparent text-xs text-slate-800 placeholder-slate-400 focus:outline-hidden w-full font-medium"
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="text-slate-400 hover:text-slate-600">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Modal Body: Complete Folios List without lost data */}
              <div className="p-4 sm:p-5 overflow-y-auto space-y-3.5 flex-1 text-slate-800">
                {filteredFolios.length === 0 ? (
                  <div className="bg-slate-50 rounded-2xl p-8 sm:p-12 text-center border border-slate-200/80 space-y-3">
                    <div className="h-12 w-12 rounded-2xl bg-white text-slate-400 flex items-center justify-center mx-auto border border-slate-200 shadow-xs">
                      <Package className="h-6 w-6" />
                    </div>
                    <h4 className="text-sm font-bold text-slate-800">
                      No hay folios activos en {currentStationDef.label}
                    </h4>
                    <p className="text-xs text-slate-500 max-w-md mx-auto">
                      Actualmente no se tienen cargas registradas en esta estación. Puedes tocar cualquiera de las otras estaciones en la barra superior para consultar sus folios.
                    </p>

                    {/* Quick phase recommendation buttons */}
                    <div className="pt-2 flex flex-wrap items-center justify-center gap-2">
                      {STATIONS.filter(st => (stationCounts[st.key] || 0) > 0).map(st => (
                        <button
                          key={st.key}
                          onClick={() => setSelectedStationKey(st.key)}
                          className="px-3 py-1.5 bg-white hover:bg-slate-100 text-slate-800 border border-slate-300 rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-2xs transition-all cursor-pointer"
                        >
                          <span>{st.label}</span>
                          <span className="px-1.5 py-0.2 bg-red-600 text-white rounded-full text-[10px] font-mono font-bold">
                            {stationCounts[st.key]}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredFolios.map((folio) => {
                      const mainItem = folio.items[0];
                      const totalPieces = folio.items.reduce((acc, it) => acc + (it.quantity || 0), 0);
                      const hasWeight = (folio as any).totalWeight || 0;

                      return (
                        <div
                          key={folio.id}
                          className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-md transition-all space-y-3"
                        >
                          {/* Header of Folio Card */}
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2 border-b border-slate-100">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="bg-slate-900 text-white font-mono font-black text-xs px-2.5 py-1 rounded-lg">
                                #{folio.id}
                              </span>
                              {(() => {
                                const badge = getStageBadgeInfo(folio.status);
                                return (
                                  <span className={`border text-[10px] font-black px-2 py-0.5 rounded-md uppercase font-mono shadow-xs ${badge.color}`}>
                                    {badge.fase}: {badge.label}
                                  </span>
                                );
                              })()}
                              {folio.costCenterName && (
                                <span className="bg-slate-100 text-slate-700 text-[10px] font-bold px-1.5 py-0.5 rounded-md">
                                  {folio.costCenterName}
                                </span>
                              )}
                              {folio.clientName && (
                                <span className="text-[11px] font-bold text-slate-600">
                                  {folio.clientName}
                                </span>
                              )}
                            </div>

                            <div className="flex items-center gap-2 text-[11px] text-slate-400 font-mono">
                              <Clock className="w-3.5 h-3.5" />
                              <span>{new Date(folio.createdDate).toLocaleDateString()} {new Date(folio.createdDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                          </div>

                          {/* Material Description & Breakdown */}
                          <div className="space-y-1.5">
                            <h4 className="text-sm font-extrabold text-slate-900 leading-snug">
                              {mainItem ? mainItem.description : 'Piezas de Decapado General'}
                            </h4>

                            {folio.items.length > 1 && (
                              <div className="bg-slate-50 rounded-xl p-2.5 border border-slate-200/80 text-xs space-y-1">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                                  Desglose de Materiales ({folio.items.length} partidas):
                                </span>
                                <div className="divide-y divide-slate-200/60">
                                  {folio.items.map((it, idx) => (
                                    <div key={idx} className="py-1 flex items-center justify-between gap-2 text-[11px]">
                                      <span className="text-slate-700 font-medium">{it.description}</span>
                                      <span className="font-mono font-bold text-slate-900 shrink-0 bg-white px-2 py-0.5 rounded border border-slate-200">
                                        {it.quantity} PZAS
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Key Data Grid: Piezas, Peso, Chofer, Centro de Costos */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-slate-50/80 p-3 rounded-xl border border-slate-100 text-xs">
                            <div>
                              <span className="text-[10px] font-bold text-slate-400 uppercase block">Total Piezas</span>
                              <span className="font-extrabold text-red-700 font-mono text-xs sm:text-sm">{totalPieces} PZAS</span>
                            </div>
                            <div>
                              <span className="text-[10px] font-bold text-slate-400 uppercase block">Peso Neto</span>
                              <span className="font-extrabold text-slate-800 font-mono text-xs sm:text-sm">
                                {hasWeight > 0 ? `${hasWeight} KGS` : 'N/A KGS'}
                              </span>
                            </div>
                            <div>
                              <span className="text-[10px] font-bold text-slate-400 uppercase block">Centro de Costo</span>
                              <span className="font-bold text-slate-700 text-xs truncate block">{folio.costCenterName || 'Principal'}</span>
                            </div>
                            <div>
                              <span className="text-[10px] font-bold text-slate-400 uppercase block">Unidad / Chofer</span>
                              <span className="font-bold text-slate-700 text-xs truncate block font-mono">
                                {folio.economicNumber || 'TRK'} • {folio.driverName || 'En patio'}
                              </span>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                            <div className="flex items-center gap-1.5 text-[11px] text-slate-500 font-medium">
                              <span className="h-2 w-2 rounded-full bg-emerald-500 shrink-0 animate-pulse"></span>
                              <span className="truncate">Certificado en Estación {currentStationDef.label}</span>
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                              {!isClientUser && (
                                <button
                                  onClick={() => {
                                    setIsPhaseFoliosModalOpen(false);
                                    setSelectedStationKey(
                                      folio.status === 'RECOLECCION' ? 'F1' :
                                      folio.status === 'ENTRADA_CEDIS' ? 'F2' :
                                      (folio.status === 'DECAPADO_TERMICO_QUIMICO' || folio.status === 'DECAPADO_LAVADO') ? 'F3' :
                                      (folio.status === 'INSPECCION' || folio.status === 'RETRABAJO') ? 'F4' :
                                      folio.status === 'SALIDA_CEDIS' ? 'F5' : 'F6'
                                    );
                                    setActiveTab('OPERATOR');
                                  }}
                                  className="bg-red-700 hover:bg-red-600 active:scale-95 text-white text-xs font-black px-2.5 py-1.5 rounded-xl transition-all flex items-center gap-1 cursor-pointer shadow-xs border border-red-500"
                                  title="Confirmar cambio de fase en pantalla de piso"
                                >
                                  <Activity className="h-3.5 w-3.5" />
                                  <span>Confirmar Fase</span>
                                </button>
                              )}

                              <button
                                onClick={() => {
                                  setIsPhaseFoliosModalOpen(false);
                                  setMapFolioModal(folio);
                                }}
                                className="bg-emerald-50 hover:bg-emerald-100 text-emerald-900 border border-emerald-300 text-xs font-black px-2.5 py-1.5 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs"
                                title="Ver mapa de ubicación GPS"
                              >
                                <Truck className="h-3.5 w-3.5 text-emerald-600 animate-pulse" />
                                <span>Mapa GPS</span>
                              </button>

                              <button
                                onClick={() => downloadFolioPDF(folio, { hidePrices: isClientUser })}
                                className="bg-red-50 hover:bg-red-100 text-red-700 text-xs font-extrabold px-2.5 py-1.5 rounded-xl transition-all flex items-center gap-1 cursor-pointer border border-red-200"
                                title="Descargar Remisión PDF con QR"
                              >
                                <Printer className="h-3.5 w-3.5" />
                                <span>PDF</span>
                              </button>

                              <button
                                onClick={() => {
                                  setIsPhaseFoliosModalOpen(false);
                                  setSelectedFolioModal(folio);
                                }}
                                className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-black px-3 py-1.5 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
                              >
                                <span>Ver Expediente</span>
                                <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="bg-slate-50 p-3.5 sm:p-4 pb-[calc(env(safe-area-inset-bottom,0px)+16px)] sm:pb-4 border-t border-slate-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 shrink-0 sticky bottom-0 z-30 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]">
                <div className="flex items-center justify-center sm:justify-start gap-2 text-xs text-slate-500 font-medium">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span className="text-[11px] sm:text-xs font-semibold">
                    Trazabilidad Oficial Grupo ISN • SNF
                  </span>
                </div>

                <button
                  onClick={() => setIsPhaseFoliosModalOpen(false)}
                  className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 active:scale-95 text-white text-xs font-black px-6 py-3 sm:py-2.5 rounded-xl transition-all cursor-pointer flex items-center justify-center gap-2 shadow-sm"
                >
                  <X className="w-4 h-4" />
                  <span>Cerrar Ventana</span>
                </button>
              </div>

            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}
